import { useNavigate, useParams } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import NegativeButton from "../../components/NegativeButton";
import PositiveButton from "../../components/PositiveButton";
import SimpleAlert from "../../components/SimpleAlert";
import TitlePicker from "../../components/Title/TitlePicker";
import useAdvertsService from "../../services/AdvertsService";
import { Advert, AdvertStats } from "../../types/Adverts";
import { HttpError } from "../../types/Error";

export default function AdminAdvertsDetailPage() {
  const navigate = useNavigate();
  const { advertId } = useParams();
  const { getAdvert, updateAdvert, deleteAdvert, createAdvert, getAdvertStats } =
    useAdvertsService();
  const [isLoading, setIsLoading] = useState(true);
  const [initiated, setInitiated] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isApproved, setIsApproved] = useState<Boolean | undefined>(false);
  const [stats, setStats] = useState<AdvertStats | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    title: "",
    destinationUrl: "",
    appStoreId: "",
    advertType: "",
    actionType: "web_url",
    startTime: "",
    endTime: "",
    titleId: "",
    image: null,
    imageUrl: "",
  });

  const deleteThisAdvert = () => {
    setIsDeleting(true);
    deleteAdvert(formData.id)
      .then(() => {
        navigate("/admin/adverts");
      })
      .catch((error) => {
        console.error("Error deleting advert:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title) {
      errors.title = "Title is required";
    }

    if (!formData.name) {
      errors.name = "Name is required";
    }

    // If the form is for a new article, ensure the image file is provided
    if (!formData.id && !formData.image) {
      errors.image = "Image file is required for new adverts.";
    }

    if (!formData.startTime) {
      errors.startTime = "Start time is required.";
    }

    if (!formData.actionType) {
      errors.actionType = "Advert type is required.";
    }

    // If the form action type is store_page, appStoreId is required
    if (formData.actionType === "store_page" && !formData.appStoreId) {
      errors.appStoreId = "App Store ID is required for store page adverts.";
    }

    // If the form action type is web_url, destinationUrl is required
    if (formData.actionType === "web_url" && !formData.destinationUrl) {
      errors.destinationUrl =
        "Destination URL is required for web URL adverts.";
    }

    if (formData.image) {
      var imageFile = formData.image as File;
      if (!imageFile.name.match(/\.(jpg|jpeg)$/)!) {
        errors.image = "Image file must be a JPG or JPEG.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveAdvert = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", formData.title);
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("destinationUrl", formData.destinationUrl || "");
    formDataToSubmit.append("appStoreId", formData.appStoreId || "");
    formDataToSubmit.append("advertType", formData.advertType || "");
    formDataToSubmit.append("actionType", formData.actionType || "web_url");
    formDataToSubmit.append("startTime", formData.startTime);
    formDataToSubmit.append("endTime", formData.endTime || "");
    formDataToSubmit.append("titleId", formData.titleId || "");

    // Load the file as a blob.
    if (formData.image) {
      const imageBlob = await new Promise<Blob>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const blob = new Blob([reader.result as ArrayBuffer]);
          resolve(blob);
        };
        reader.readAsArrayBuffer(formData.image!);
      });
      formDataToSubmit.append("Image", imageBlob);
    }

    var savedAdvert: Advert | null = null;

    if (formData.id) {
      var updateResult = await updateAdvert(formData.id, formDataToSubmit);

      savedAdvert = updateResult as Advert;

      // If the result is a http error
      if (!savedAdvert) {
        alert("Error creating advert");
        setIsSaving(false);
        return;
      }
    } else {
      var createResult = await createAdvert(formDataToSubmit);
      savedAdvert = createResult as Advert;

      if (!savedAdvert) {
        alert("Error creating advert");
        setIsSaving(false);
        return;
      }
    }

    setFormData({
      ...formData,
      id: savedAdvert.id,
      imageUrl: savedAdvert.imageUrl!,
    });

    setIsClean(true);
    setIsSaving(false);

    const cameFromNew = advertId?.toLowerCase() === "new";

    if (cameFromNew) {
      navigate(`/admin/adverts/${savedAdvert.id}`, { replace: true });
    }
  };

  const isFormValid = submitted && Object.keys(validationErrors).length === 0;

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const handleImageFileChange = (e: any) => {
    const { files } = e.target;

    setIsClean(false);

    if (!files || files.length === 0) {
      setFormData({
        ...formData,
        image: null,
      });

      setPreviewBlob(null);

      return;
    }

    setFormData({
      ...formData,
      image: files[0],
    });

    // Load the image and set the preview blob.
    const reader = new FileReader();
    reader.onload = () => {
      const blob = new Blob([reader.result as ArrayBuffer]);
      setPreviewBlob(blob);
    };
    reader.readAsArrayBuffer(files[0]);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (initiated) {
      return;
    }

    setIsLoading(true);
    setInitiated(true);

    if (advertId?.toLowerCase() == "new") {
      setIsLoading(false);
    } else {

      getAdvertStats(advertId as string)
        .then((statsResult) => {
          setStats(statsResult as AdvertStats);
        });

      getAdvert(advertId as string)
        .then((result) => {
          var advert = result as Advert;

          // Dates are in the format "2025-10-24T00:00:00".

          // Convert start and end time  for datetime-local input
          var startTime = advert.startTime.toString();
          var endTime = advert.endTime == null ? "" : advert.endTime.toString();

          setFormData({
            id: advert.id,
            title: advert.title,
            name: advert.name,
            image: null,
            startTime: startTime,
            endTime: endTime,
            titleId: advert.titleId || "",
            imageUrl: advert.imageUrl || "",
            destinationUrl: advert.destinationUrl || "",
            appStoreId: advert.appStoreId || "",
            advertType: advert.advertType || "",
            actionType: advert.actionType || "",
          });
        })
        .catch((error) => {
          console.error("Error fetching advert:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initiated]);

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <div className="flex flex-row">
        <BackButton />
        <PageTitle className="text-4xl">
          {isLoading ? "Loading" : formData.id ? formData.title : "New Advert"}
          {isLoading && <LoadingEllipsis />}
        </PageTitle>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-2 col-span-2">
          <h2 className="text-2xl">Details</h2>
          {stats && (
            <div className="grid grid-cols-2 gap-4 w-full">
              <div className="card p-8">{stats.impressions} Impressions</div>
              <div className="card p-8">{stats.clicks} Clicks</div>
            </div>
          )}
          {isLoading && <Ghost additionalClasses="h-24 col-span-2 rounded" />}
          {!isLoading && formData && (
            <form onSubmit={saveAdvert} className="form">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 xl:grid-cols-2">
                <div className="card p-8 flex flex-col gap-4">
                  <div className="">
                    <label htmlFor="name" className="block leading-6">
                      Advert name (internal use) *
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="name"
                      placeholder="Advert name "
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "name"
                      )}`}
                    />

                    {validationError("name") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("name")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="title" className="block leading-6">
                      Advert title (shown in app) *
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="title"
                      placeholder="Advert title *"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "title"
                      )}`}
                    />

                    {validationError("title") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("title")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="advertType" className="block leading-6">
                      Advert type (shown in app as a badge on the advert)
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="advertType"
                      placeholder="Promoted game"
                      value={formData.advertType}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "advertType"
                      )}`}
                    />

                    {validationError("advertType") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("advertType")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <TitlePicker
                      label="Game Title"
                      value={formData.titleId}
                      required={false}
                      disabled={isSaving}
                      placeholder="Type to search for a title"
                      error={validationError("titleId")}
                      onChange={(id) => {
                        console.log("TitlePicker onChange:", id);
                        setIsClean(false);
                        setFormData((prev) => ({ ...prev, titleId: id || "" }));
                      }}
                    />

                    {validationError("titleId") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("titleId")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="startTime" className="block leading-6">
                      Advert start time *
                    </label>

                    <input
                      disabled={isSaving}
                      type="datetime-local"
                      name="startTime"
                      placeholder="Advert start time"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "startTime"
                      )}`}
                    />

                    {validationError("startTime") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("startTime")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="endTime" className="block leading-6">
                      Advert end time
                    </label>

                    <input
                      disabled={isSaving}
                      type="datetime-local"
                      name="endTime"
                      placeholder="Advert end time"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "endTime"
                      )}`}
                    />

                    {validationError("endTime") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("endTime")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="actionType" className="block leading-6">
                      Advert type (where should the click send the user) *
                    </label>

                    <select
                      disabled={isSaving}
                      name="actionType"
                      value={formData.actionType}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "actionType"
                      )}`}
                    >
                      <option value="">Select advert type</option>
                      <option value="store_page">Meta App Store Page</option>
                      <option value="web_url">Web url</option>
                    </select>

                    {validationError("actionType") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("actionType")}
                      </div>
                    )}
                  </div>
                  {formData.actionType === "web_url" && (
                    <div className="">
                      <label
                        htmlFor="destinationUrl"
                        className="block leading-6"
                      >
                        Advert destination URL *
                      </label>

                      <input
                        disabled={isSaving}
                        type="url"
                        name="destinationUrl"
                        placeholder="https://example.com"
                        value={formData.destinationUrl}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                          "destinationUrl"
                        )}`}
                      />

                      {validationError("destinationUrl") && (
                        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                          {validationError("destinationUrl")}
                        </div>
                      )}
                    </div>
                  )}
                  {formData.actionType === "store_page" && (
                    <div className="">
                      <label htmlFor="appStoreId" className="block leading-6">
                        App Store ID *
                      </label>

                      <input
                        disabled={isSaving}
                        type="text"
                        name="appStoreId"
                        placeholder="10058771020910978"
                        value={formData.appStoreId}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                          "appStoreId"
                        )}`}
                      />

                      {validationError("appStoreId") && (
                        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                          {validationError("appStoreId")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="card p-8 flex flex-col gap-4">
                  {formData.imageUrl && !previewBlob && (
                    <div className="flex flex-col gap-2">
                      <img
                        src={formData.imageUrl}
                        alt="Advert"
                        className="w-full h-auto rounded"
                      />
                    </div>
                  )}
                  {previewBlob && (
                    <div className="flex flex-col gap-2">
                      <img
                        src={URL.createObjectURL(previewBlob)}
                        alt="Advert"
                        className="w-full h-auto rounded"
                      />
                    </div>
                  )}
                  <label htmlFor="image" className="block leading-6 pt-4">
                    Advert image (JPG or JPEG) *
                  </label>

                  <input
                    disabled={isSaving}
                    accept="image/jpg,image/jpeg"
                    type="file"
                    name="image"
                    onChange={handleImageFileChange}
                    className={`w-full px-3 py-2 border rounded text-white ${validStyle(
                      "image"
                    )}`}
                  />

                  {validationError("image") && (
                    <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                      {validationError("image")}
                    </div>
                  )}
                </div>
              </div>
              <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
                {formData.id && (
                  <NegativeButton
                    type="button"
                    disabled={isSaving}
                    className={`text-red-500 py-2 px-4 rounded border-red-900 border hover:bg-red-900/25 `}
                    onClick={() => setDeleteAlertOpen(true)}
                  >
                    Delete advert
                  </NegativeButton>
                )}
                <PositiveButton type="submit" disabled={isSaving || isClean}>
                  {isSaving
                    ? "Saving..."
                    : formData.id && isClean
                    ? "Saved"
                    : "Save changes"}
                </PositiveButton>
              </div>
            </form>
          )}
        </div>
      </div>
      <SimpleAlert
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title={`Are you sure you want to delete this advert?`}
        message="This action cannot be undone and it will destroy all associated impressions and links! If you just want the advert to stop showing, consider setting an end date in the past instead."
        confirmText={`Delete ${formData.title}`}
        onConfirm={deleteThisAdvert}
      />
    </section>
  );
}
